import { useCallback, useRef, useState, type DragEvent } from 'react';
import { ApiFailure, api, putBytes } from '../api.ts';
import { formatBytes, readDurationSec } from '../duration.ts';

/**
 * GETTING AUDIO IN — and NOTHING ELSE. The owner's split, after watching a
 * failed upload leave a corpse: "I upload, then I select the engine and run
 * the AI… each step is separate." So this page has no pipeline picker any
 * more: it puts bytes in and parks the recording as `uploaded`; the engine is
 * chosen — and re-chosen, as often as wanted — on the recording itself, where
 * "run the first pass" lives.
 *
 * Three steps, and only two of them touch the manager:
 *
 *   1. POST /admin/recordings                -> a row and a PUT url
 *   2. PUT the bytes to that url             -> straight to object storage
 *   3. POST /recordings/:id/confirm queue:no -> bytes acknowledged, PARKED
 *
 * A FAILED FILE CLEANS UP AFTER ITSELF. The row is written before the bytes
 * fly, so a failed PUT used to strand a stub in the list ("why it stayed??").
 * Now a failure keeps the File in hand and offers TRY AGAIN (delete the stub,
 * start that file over) and REMOVE (delete the stub, drop the entry) — and a
 * server-side sweep catches anything a closed tab abandons.
 *
 * FILES ARE UPLOADED ONE AT A TIME, deliberately. Dropping six sessions and
 * firing six concurrent PUTs splits one connection six ways: every file
 * finishes at roughly the same late moment instead of the first finishing
 * early. Serial means the queue drains visibly from the top. A failed file
 * does not stop the queue — one bad file out of six is a reason to report
 * that file, not to abandon the other five.
 */

type Phase =
  | { at: 'waiting' }
  | { at: 'measuring' }
  | { at: 'creating' }
  | { at: 'uploading'; fraction: number }
  | { at: 'confirming' }
  | { at: 'done' }
  | { at: 'failed'; message: string };

interface Job {
  /** Local only. The server's recording id does not exist until step 1. */
  key: string;
  name: string;
  sizeBytes: number;
  phase: Phase;
  /** Kept for Try again — a retry needs the bytes, not just the name. */
  file: File;
  /** Set once step 1 lands; what Try again and Remove clean up. */
  recordingId?: string;
}

/** What the file picker offers, and what a drop is checked against. The
 *  storage layer whitelists the same set when it builds the object key
 *  (`AUDIO_EXTENSIONS` in `storage/r2.ts`) — a file outside it would upload
 *  fine and then be stored under a `.wav` key it is not. */
const ACCEPT = '.wav,.m4a,.mp3,.ogg,.opus,.webm,.flac,.aac,.mp4,.caf,audio/*';
const EXTENSIONS = new Set(['wav', 'm4a', 'mp3', 'ogg', 'opus', 'webm', 'flac', 'aac', 'mp4', 'caf']);

function looksLikeAudio(file: File): boolean {
  if (file.type.startsWith('audio/')) return true;
  const dot = file.name.lastIndexOf('.');
  return dot > 0 && EXTENSIONS.has(file.name.slice(dot + 1).toLowerCase());
}

function phaseText(phase: Phase): string {
  switch (phase.at) {
    case 'waiting':
      return 'waiting';
    case 'measuring':
      return 'reading length…';
    case 'creating':
      return 'reserving…';
    case 'uploading':
      return `uploading ${Math.round(phase.fraction * 100)}%`;
    case 'confirming':
      return 'finishing…';
    case 'done':
      return 'uploaded';
    case 'failed':
      return 'failed';
  }
}

function phaseFraction(phase: Phase): number {
  switch (phase.at) {
    case 'waiting':
      return 0;
    case 'measuring':
      return 0.02;
    case 'creating':
      return 0.05;
    // The PUT is ~90% of the wall time, so it gets ~90% of the bar. A bar that
    // gave each of the six steps a sixth would sit at 50% for four minutes.
    case 'uploading':
      return 0.05 + phase.fraction * 0.9;
    case 'confirming':
      return 0.97;
    case 'done':
      return 1;
    case 'failed':
      return 1;
  }
}

export function Upload({
  token,
  projectId,
  /** Only for the sentence under the heading. The picker itself is the shell's,
   *  because all four pages are about the same project. */
  projectLabel,
  onUploaded,
  onLanded,
  onSignedOut,
}: {
  token: string;
  projectId: string;
  projectLabel: string;
  onUploaded: () => void;
  /**
   * THE BATCH ARRIVED, ALL OF IT. The shell answers this by opening the first
   * recording — where "run the first pass" now lives, engine picker and all.
   * ONLY ON A CLEAN BATCH: navigating away with one file failed would take
   * its error (and its Try again) off the screen.
   */
  /** Optional since the project page: uploads land in the cards right below,
   *  so there is nowhere to navigate to. */
  onLanded?: (landed: { recordingId: string; name: string; count: number }) => void;
  onSignedOut: () => void;
}): JSX.Element {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const fileInput = useRef<HTMLInputElement>(null);
  /** Guards against a second drop starting a second serial run over the top of
   *  the first — `busy` is state and is a render behind a fast double drop. */
  const running = useRef(false);

  const patchJob = useCallback((key: string, patch: Partial<Job>) => {
    setJobs((prev) => prev.map((j) => (j.key === key ? { ...j, ...patch } : j)));
  }, []);

  /** One file, start to parked. Shared by the batch loop and Try again. */
  const uploadOne = useCallback(
    async (key: string, file: File): Promise<string | null> => {
      try {
        patchJob(key, { phase: { at: 'measuring' } });
        const durationSec = await readDurationSec(file);

        patchJob(key, { phase: { at: 'creating' } });
        const created = await api.createRecording(token, {
          projectId,
          name: file.name,
          durationSec,
          sizeBytes: file.size,
        });
        patchJob(key, { recordingId: created.recordingId });

        patchJob(key, { phase: { at: 'uploading', fraction: 0 } });
        await putBytes(created.uploadUrl, file, (fraction) =>
          patchJob(key, { phase: { at: 'uploading', fraction } }),
        );

        /*
         * CONFIRM — WITHOUT QUEUEING. `queue: false` acknowledges the bytes
         * and leaves the recording parked in `uploaded`; running the first
         * pass is its own step, on the recording, with the engine chosen
         * there. Without this call the sweeper would treat the row as a
         * corpse even though the bytes are fine.
         */
        patchJob(key, { phase: { at: 'confirming' } });
        await api.confirmUpload(token, created.recordingId, { queue: false });

        patchJob(key, { phase: { at: 'done' } });
        onUploaded();
        return created.recordingId;
      } catch (err) {
        if (err instanceof ApiFailure && err.status === 401) {
          onSignedOut();
          return null;
        }
        const message =
          err instanceof ApiFailure
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Upload failed.';
        patchJob(key, { phase: { at: 'failed', message } });
        return null;
      }
    },
    [projectId, token, patchJob, onUploaded, onSignedOut],
  );

  const run = useCallback(
    async (files: File[]) => {
      if (running.current) return;

      const audio = files.filter(looksLikeAudio);
      const rejected = files.length - audio.length;
      setNotice(
        rejected > 0
          ? `${rejected} file${rejected === 1 ? '' : 's'} skipped — not audio this pipeline reads.`
          : null,
      );
      if (audio.length === 0) return;
      if (!projectId) {
        setNotice('Pick a project first.');
        return;
      }

      const fresh: Job[] = audio.map((f, i) => ({
        key: `${Date.now()}-${i}-${f.name}`,
        name: f.name,
        sizeBytes: f.size,
        phase: { at: 'waiting' },
        file: f,
      }));
      setJobs(fresh);
      running.current = true;
      setBusy(true);

      let landed = 0;
      /** The FIRST one in — the one worth opening when the batch is clean. */
      let first: { recordingId: string; name: string } | null = null;
      for (const job of fresh) {
        const recordingId = await uploadOne(job.key, job.file);
        if (recordingId) {
          landed += 1;
          first ??= { recordingId, name: job.name };
        }
      }

      running.current = false;
      setBusy(false);
      if (landed > 0) onUploaded();
      // Every file, or nobody moves. See `onLanded`.
      if (first && landed === audio.length) {
        onLanded?.({ recordingId: first.recordingId, name: first.name, count: landed });
      }
    },
    [projectId, uploadOne, onUploaded, onLanded],
  );

  /** Try again: clear the stub this failure left, then run the same file. */
  const retry = useCallback(
    async (job: Job) => {
      if (running.current) return;
      running.current = true;
      setBusy(true);
      if (job.recordingId) {
        try {
          await api.deleteRecording(token, job.recordingId, job.name);
        } catch {
          /* a stub the server refuses to drop is the sweeper's problem */
        }
        patchJob(job.key, { recordingId: undefined });
        onUploaded();
      }
      const recordingId = await uploadOne(job.key, job.file);
      running.current = false;
      setBusy(false);
      if (recordingId) {
        onUploaded();
        onLanded?.({ recordingId, name: job.name, count: 1 });
      }
    },
    [token, patchJob, uploadOne, onUploaded, onLanded],
  );

  /** Remove: clear the stub and forget the entry. */
  const remove = useCallback(
    async (job: Job) => {
      if (job.recordingId) {
        try {
          await api.deleteRecording(token, job.recordingId, job.name);
          onUploaded();
        } catch {
          /* the sweeper catches it */
        }
      }
      setJobs((prev) => prev.filter((j) => j.key !== job.key));
    },
    [token, onUploaded],
  );

  function onDrop(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setOver(false);
    if (busy) return;
    void run(Array.from(e.dataTransfer.files));
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2>Upload audio</h2>
      </div>
      <p className="hint">
        Into <strong>{projectLabel || 'the selected project'}</strong>. The bytes go straight to
        storage — never through the API. Once a file is in, open it and run the AI's first pass
        with whichever pipeline you choose; you can run it again later with a different one.
      </p>

      {notice && (
        <div className="notice notice-warn" role="status">
          {notice}
        </div>
      )}

      <div
        className={`drop${over ? ' is-over' : ''}${busy ? ' is-busy' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => !busy && fileInput.current?.click()}
        onKeyDown={(e) => {
          if (busy) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInput.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <div className="drop-title">{busy ? 'Uploading…' : 'Drop audio here, or click to choose'}</div>
        <div className="drop-sub">wav · m4a · mp3 · ogg · opus · webm · flac · aac · caf</div>
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            // Cleared so choosing the SAME file twice in a row still fires a
            // change event — otherwise a retry after a failure does nothing.
            e.target.value = '';
            void run(picked);
          }}
        />
      </div>

      {jobs.length > 0 && (
        <ul className="jobs">
          {jobs.map((j) => {
            const failed = j.phase.at === 'failed';
            const done = j.phase.at === 'done';
            return (
              <li className="job" key={j.key}>
                <div className="job-head">
                  <span className="job-name">{j.name}</span>
                  <span className="job-phase num">{formatBytes(j.sizeBytes)}</span>
                  <span
                    className={`job-phase num${done ? ' is-done' : ''}${failed ? ' is-error' : ''}`}
                  >
                    {phaseText(j.phase)}
                  </span>
                </div>
                <div className="bar">
                  <div
                    className={`bar-fill${failed ? ' is-error' : ''}`}
                    style={{ width: `${Math.round(phaseFraction(j.phase) * 100)}%` }}
                  />
                </div>
                {j.phase.at === 'failed' && (
                  <div className="job-foot">
                    <span className="job-error">{j.phase.message}</span>
                    <span className="job-actions">
                      <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void retry(j)}>
                        Try again
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        disabled={busy}
                        onClick={() => void remove(j)}
                      >
                        Remove
                      </button>
                    </span>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
